/* eslint-disable @typescript-eslint/no-unsafe-return */
import React, { useEffect, useState } from 'react';
import {
  AlertBanner,
  Box,
  Loader,
  TabContent,
  TabGroup,
  TabItem,
  Typography,
} from '@saviynt/design-system';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import useGetIDProviders from '../../../../hooks/use-getidproviders';
import { type RootState } from '../../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../../utils';
import { ERROR_MESSAGE } from '../../../../utils/constants';
import { providers } from '../../data-layer/constants';
import { type IDPConfig, type IDPConfigKosmos } from '../../data-layer/types';
import { useFetchConfig, useUpdateConfig } from '../../hooks';
import ProviderCard from '../common/ProviderCard/ProviderCard';
import IDPSettings from '../IDPSettings/IDPSettings';
import SetUpIdProofing from '../SetUpIdProofing/SetUpIdProofing';

import invertedMessages from './messages';

import './IDPProviders.css';

export type FormData = Record<string, string>;
export type ErrorMessages = Record<string, string>;

export default function IDPProviders() {
  const [isShowAlertBanner, setIsShowAlertBanner] = useState(false);
  const [isEnableSuccessVisible, setIsEnableSuccessVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertStatus, setAlertStatus] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [verificationExpireTime, setVerificationExpireTime] = useState('');

  const l = useInvertedLocalize(invertedMessages);
  const location = useLocation();
  const history = useHistory();
  const data = location.state as { title?: string; action?: string };
  const { isMenuPinned } = useSelector((state: RootState) => state.user);

  const {
    data: idpProviders,
    isLoading: isIDPProvidersLoading,
    isError,
    error,
    refetch,
  } = useGetIDProviders();

  const refetchIDPProviders = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refetch();
  };

  useEffect(() => {
    if (data && data.title && data.action) {
      setIsEnableSuccessVisible(true);
      setAlertTitle(data.title);
      setAlertStatus(data.action);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refetch();
    }

    history.replace({ pathname: '/providers', state: null });
  }, [history, data, refetch]);

  const availableProviders = providers.filter(
    (obj1) => !idpProviders?.some((obj2) => obj1.id === obj2.vendorName)
  );

  const handleConfigureClick = (provider: IDPConfig | IDPConfigKosmos) => {
    history.push({
      pathname: `/providers/settings`,
      search: `?id=${provider.vendorName}`,
    });
  };

  const handleChange = (value: string) => setActiveTab(value);

  useEffect(() => {
    if (isError) {
      setErrorMessage(error.errorMessage ?? ERROR_MESSAGE);
      setShowCriticalBanner(true);
    }
  }, [isError, error]);

  const onCancel = () => {
    setIsEnableSuccessVisible(false);
  };

  const renderSuccessAlertBanner = isEnableSuccessVisible ? (
    <Box className='Ext-IDPProviders-enable-successAlert'>
      <AlertBanner
        colorTheme='Success'
        title={`${alertTitle} ${alertStatus}`}
        description={
          alertStatus === 'Enabled'
            ? l(
                `You can now configure user modification workflow to include identity verification workflow stage.`
              )
            : ''
        }
        isColonVisible={alertStatus === 'Enabled'}
        isVisible={isEnableSuccessVisible}
        onCancel={onCancel}
        shouldAutoDismiss
      />
    </Box>
  ) : (
    ''
  );

  const renderUpdateSuccessbanner = showSuccessBanner ? (
    <Box className='Ext-IDPProviders-enable-successAlert'>
      <AlertBanner
        colorTheme='Success'
        title={l(`Settings have been updated successfully.`)}
        description=''
        isColonVisible={alertStatus === 'Enabled'}
        isVisible={showSuccessBanner}
        onCancel={() => setShowSuccessBanner(false)}
        shouldAutoDismiss
        autoDismissDelay={3000}
      />
    </Box>
  ) : (
    ''
  );

  const renderErrorAlertBanner = showCriticalBanner ? (
    <Box className='Ext-IDPProviders-errorBanner'>
      <AlertBanner
        colorTheme='Critical'
        title={errorMessage}
        description=''
        isVisible={showCriticalBanner}
        onCancel={() => setShowCriticalBanner(false)}
        isColonVisible={false}
        isFloating
        position={{
          type: 'absolute',
          top: '7rem',
          left: '7rem',
        }}
        shouldAutoDismiss
      />
    </Box>
  ) : (
    ''
  );

  const { data: configData } = useFetchConfig();

  useEffect(() => {
    if (configData && configData.configkeys.length > 0) {
      const configItem = configData.configkeys.find(
        (item) => item.configname === 'ID_PROOFING_SESSION_EXPIRE_HOURS'
      );

      if (configItem) {
        setVerificationExpireTime(configItem.configdata);
      }
    }
  }, [configData]);

  const { mutate: updateConfigMutation, isPending: isUpdateInProgress } =
    useUpdateConfig();

  const handleSave = () => {
    updateConfigMutation(
      {
        configkeys: [
          {
            configname: 'ID_PROOFING_SESSION_EXPIRE_HOURS',
            configdata: verificationExpireTime,
          },
        ],
      },
      {
        onSuccess: (response) => {
          if (response.error && response.error.errorMessage) {
            setErrorMessage(response.error.errorMessage);

            setShowCriticalBanner(true);

            return;
          }

          setShowSuccessBanner(true);
        },
      }
    );
  };

  const isSaveDisabled = isEmpty(verificationExpireTime) || isUpdateInProgress;

  return (
    <div>
      <AlertBanner
        dataTestId='Ext-IDPProviders-alert'
        colorTheme='Critical'
        title={l('Data Fetch Error')}
        description={l(
          'Identity Verification configuration Action failed, please try again later.'
        )}
        isVisible={isShowAlertBanner}
        onCancel={() => setIsShowAlertBanner(false)}
        shouldAutoDismiss
      />
      {renderErrorAlertBanner}
      {renderUpdateSuccessbanner}
      {isIDPProvidersLoading && (
        <div className='Ext-IDPProviders-loadingContainer'>
          <Loader kind='loop' format='main' color='brand' size='large' />
        </div>
      )}
      {!isIDPProvidersLoading && idpProviders && idpProviders.length > 0 && (
        <Box className='Ext-IDPProviders'>
          {renderSuccessAlertBanner}
          <div className='Ext-IDPProviders-title'>
            <Typography kind='h1' color='--color-foreground-neutral-bold'>
              {l('Identity Verification')}
            </Typography>
          </div>
          <div>
            <Box>
              <TabGroup
                className='Ext-IDPProviders-tabGroup'
                value={activeTab}
                onChange={handleChange}>
                <TabItem label={l('Providers')} value='1' />
                <TabItem label={l('Settings')} value='2' />
              </TabGroup>
              <TabContent value={activeTab} tabValue='1'>
                <Box className='Ext-IDPProviders-tabcontent-providers'>
                  <Box
                    className='Ext-IDPProviders-tabcontent-providers-configured-header'
                    dataTestId='Ext-IDPProviders-tabcontent-providers-configured-header'>
                    <Typography
                      kind='h2'
                      color='--color-foreground-neutral-bold'>
                      {l('Configured Providers')}
                    </Typography>
                  </Box>
                  <div className='Ext-IDPProviders-contentWrapper-content'>
                    {idpProviders.map((item) => (
                      <ProviderCard
                        key={item.vendorName}
                        provider={providers.find(
                          (prov) => prov.id === item.vendorName
                        )}
                        item={item}
                        setErrorMessage={setErrorMessage}
                        setShowCriticalBanner={setShowCriticalBanner}
                        setIsEnableSuccessVisible={setIsEnableSuccessVisible}
                        setAlertTitle={setAlertTitle}
                        setAlertStatus={setAlertStatus}
                        handleConfigureClick={handleConfigureClick}
                        refetchIDPProviders={refetchIDPProviders}
                      />
                    ))}
                  </div>
                  {!isIDPProvidersLoading && availableProviders.length > 0 ? (
                    <div>
                      <Box
                        className='Ext-IDPProviders-tabcontent-providers-available-header'
                        dataTestId='Ext-IDPProviders-tabcontent-providers-available-header'>
                        <Typography
                          kind='h2'
                          color='--color-foreground-neutral-bold'>
                          {l('Available Providers')}
                        </Typography>
                      </Box>
                      <Box className='Ext-IDPProviders-contentWrapper'>
                        <div className='Ext-IDPProviders-contentWrapper-content'>
                          {availableProviders.map((item) => (
                            <ProviderCard
                              provider={providers.find(
                                (prov) => prov.id === item.vendorName
                              )}
                              item={item}
                              setErrorMessage={setErrorMessage}
                              setShowCriticalBanner={setShowCriticalBanner}
                              setIsEnableSuccessVisible={
                                setIsEnableSuccessVisible
                              }
                              setAlertTitle={setAlertTitle}
                              setAlertStatus={setAlertStatus}
                              handleConfigureClick={handleConfigureClick}
                              refetchIDPProviders={refetchIDPProviders}
                            />
                          ))}
                        </div>
                      </Box>
                    </div>
                  ) : (
                    ''
                  )}
                </Box>
              </TabContent>
              <TabContent value={activeTab} tabValue='2'>
                <IDPSettings
                  value={verificationExpireTime}
                  setValue={setVerificationExpireTime}
                  isMenuPinned={isMenuPinned}
                  onSave={handleSave}
                  isSaving={isUpdateInProgress}
                  isSaveDisabled={isSaveDisabled}
                />
              </TabContent>
            </Box>
          </div>
        </Box>
      )}
      {!isIDPProvidersLoading && idpProviders?.length === 0 && (
        <SetUpIdProofing />
      )}
    </div>
  );
}
